/**
 * Service: Organization settings data access.
 * Handles all Supabase interactions for organization CRUD.
 * All functions throw on error so callers can handle failures.
 */

import { supabase } from "@/integrations/supabase/client";

export interface OrgSettings {
  name: string;
  default_language: string;
  available_languages: string[];
}

/**
 * Predefined icon keys usable on a proficiency level.
 * Legacy 4 keys are still consumed by the scoring engine; additional keys are
 * display-only catalog entries used in the proficiency-levels admin UI.
 * The string type is intentional to allow future additions without DB migrations.
 */
export type ProficiencyIconKey = string;

/** A single configurable proficiency level. */
export interface ProficiencyLevel {
  id: string;
  label: string;
  icon: ProficiencyIconKey;
  /** Data URL of a user-uploaded custom icon (overrides `icon` when present). */
  iconUrl?: string;
  /** Hex color override for the icon (falls back to the catalog color). */
  color?: string;
}

/** A single configurable performance-score level (name + description). */
export interface PerformanceLevel {
  id: string;
  label: string;
  description: string;
  icon: ProficiencyIconKey;
  iconUrl?: string;
  /** Hex color override for the icon (falls back to the catalog color). */
  color?: string;
}

/**
 * Stored shape in `organizations.proficiency_level_labels` (jsonb).
 * Legacy keys (achieved/almostAchieved/evolution/notMet) remain readable for
 * backward compatibility; new code should read/write `levels`.
 */
export interface ProficiencyLevelLabels {
  achieved?: string;
  almostAchieved?: string;
  evolution?: string;
  notMet?: string;
  levels?: ProficiencyLevel[];
  performanceLevels?: PerformanceLevel[];
}

/** Fetch organization settings. */
export async function fetchOrgSettings(orgId: string): Promise<OrgSettings | null> {
  const { data, error } = await supabase
    .from("organizations")
    .select("name, default_language, available_languages")
    .eq("id", orgId)
    .maybeSingle();
  if (error) throw new Error(`Impossible de charger les paramètres de l'organisation : ${error.message}`);
  if (!data) return null;
  return {
    name: data.name || "",
    default_language: data.default_language || "fr",
    available_languages: Array.isArray(data.available_languages) ? data.available_languages : ["fr"],
  };
}

/** Update organization settings. Returns the updated data or throws. */
export async function updateOrgSettings(orgId: string, settings: Partial<OrgSettings>): Promise<void> {
  const { error } = await supabase
    .from("organizations")
    .update(settings)
    .eq("id", orgId)
    .select()
    .maybeSingle();
  if (error) throw error;
}

// ─── Proficiency level labels ───────────────────────────────────────

/** Fetch custom proficiency level labels for an organization. */
export async function fetchProficiencyLevelLabels(orgId: string): Promise<ProficiencyLevelLabels> {
  const { data, error } = await supabase
    .from("organizations")
    .select("proficiency_level_labels")
    .eq("id", orgId)
    .maybeSingle();
  if (error) throw new Error(`Impossible de charger les libellés : ${error.message}`);
  return (data?.proficiency_level_labels as ProficiencyLevelLabels) ?? {};
}

/** Persist custom proficiency level labels. */
export async function saveProficiencyLevelLabels(orgId: string, labels: ProficiencyLevelLabels): Promise<void> {
  const { error } = await supabase
    .from("organizations")
    .update({ proficiency_level_labels: labels } as never)
    .eq("id", orgId);
  if (error) throw new Error(`Impossible de sauvegarder les libellés : ${error.message}`);
}
